import { getImageProps } from 'next/image';
import { useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';
import { POSTER_SIZES } from '../_const/stories';

/**
 * 스토리 모달 View Transition. 열기는 임시 모달을 flushSync 로 그려 새 화면으로 삼고, 이전/다음은
 * markStoryRendered 까지 스냅샷을 미룬다. 미지원 브라우저·감속 설정이면 그냥 이동한다
 */

// 카드 사진 → 모달 미디어를 잇는 이름(모달 미디어는 .ty-story-media 로 늘 이 이름을 갖는다)
const MEDIA_NAME = 'ty-story-media';
// 렌더 신호가 오지 않아도 화면이 멈춰 있지 않게 하는 상한
const RENDER_TIMEOUT_MS = 1500;
// 라우트 모달이 끝내 오지 않아도(전체 새로고침 이동 등) 임시 모달이 남지 않게 하는 상한
const OPENING_TIMEOUT_MS = 8000;

// 본문을 미는 방향(story-transition.css). 열기는 방향이 없다
export type StoryDirection = 'prev' | 'next';

let finishPending: (() => void) | null = null;

/** 이전/다음으로 바뀐 스토리가 DOM 에 반영됐다고 알린다(모달 내용의 layout effect 에서 호출) */
export function markStoryRendered() {
  finishPending?.();
  finishPending = null;
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// 이미 받은 카드 사진 주소(Day 별). 공개 카드만 누를 수 있어 미공개 에셋은 들어오지 않는다
const loadedPhotos = new Map<number, string>();

// 참조를 쥐어야 디코딩 결과가 남는다. 비트맵이 장당 수 MB 라 최근 몇 장만(밀려나면 카드 사진으로)
const WARM_POSTER_LIMIT = 6;
const warmPosters = new Map<
  string,
  { image: HTMLImageElement; ready: boolean }
>();

/** 호버·포커스 때 포스터를 받아 디코딩해 둔다 — 누를 때 있으면 전환이 카드 사진에서 포스터로 바로 이어진다 */
export function warmStoryPoster(src: string) {
  if (warmPosters.has(src)) return;
  const { props } = getImageProps({
    src,
    alt: '',
    fill: true,
    sizes: POSTER_SIZES,
  });
  const image = new window.Image();
  // srcset 을 src 보다 먼저 — 순서가 바뀌면 src 로 한 번 더 받는다
  image.sizes = POSTER_SIZES;
  if (props.srcSet) image.srcset = props.srcSet;
  image.src = props.src;
  const entry = { image, ready: false };
  warmPosters.set(src, entry);
  if (warmPosters.size > WARM_POSTER_LIMIT) {
    warmPosters.delete(warmPosters.keys().next().value!);
  }
  image.decode().then(
    function markReady() {
      entry.ready = true;
    },
    function dropFailed() {
      warmPosters.delete(src);
    },
  );
}

/** 밑깔개: 디코딩된 포스터, 없으면 카드 사진. srcset 없이 그려야 전환 스냅샷에 바로 찍힌다 */
export function getStoryBackdrop(day: number, poster: string) {
  const warm = warmPosters.get(poster);
  return warm?.ready ? warm.image.currentSrc : loadedPhotos.get(day);
}

// 여는 중인 Day — 라우트 모달이 도착할 때까지 임시 모달(StoryDialogPlaceholder)이 그린다
let opening: number | null = null;
let openingTimer: ReturnType<typeof setTimeout> | undefined;
const openingListeners = new Set<() => void>();

function setOpening(next: number | null) {
  opening = next;
  clearTimeout(openingTimer);
  if (next !== null) {
    openingTimer = setTimeout(clearOpeningStory, OPENING_TIMEOUT_MS);
  }
  openingListeners.forEach(function notify(listener) {
    listener();
  });
}

function subscribeOpening(listener: () => void) {
  openingListeners.add(listener);
  return function unsubscribe() {
    openingListeners.delete(listener);
  };
}

function getOpening() {
  return opening;
}

function noOpeningOnServer() {
  return null;
}

export function useOpeningStory() {
  return useSyncExternalStore(subscribeOpening, getOpening, noOpeningOnServer);
}

/** 임시 모달이 빠질 때(슬롯이 라우트 모달로 바뀔 때) 부른다 — 뒤로가기로 돌아와도 다시 뜨지 않게 */
export function clearOpeningStory() {
  if (opening !== null) setOpening(null);
}

/** 카드 → 모달. 사진 주소를 기억하고, 임시 모달을 띄우고, 카드 사진(frame)이 모달 미디어로 이어지게 전환한다 */
export function openStory(
  day: number,
  frame: HTMLElement | null,
  navigate: () => void,
) {
  const photo = frame?.querySelector('img')?.currentSrc;
  if (photo) loadedPhotos.set(day, photo);
  startStoryTransition(
    function showOverlayAndNavigate() {
      // 임시 모달을 지금 그려 새 화면으로 삼는다. 라우트 모달은 도착하면 슬롯 교체로 자리를 넘겨받는다
      flushSync(function showOverlay() {
        setOpening(day);
      });
      navigate();
    },
    { from: frame },
  );
}

export function switchStory(direction: StoryDirection, navigate: () => void) {
  startStoryTransition(
    function navigateAndWaitForRender() {
      return new Promise<void>(function waitForStoryRender(resolve) {
        finishPending = resolve;
        navigate();
        // 자기 전환만 포기한다 — 연달아 넘길 때 앞 타이머가 다음 전환을 일찍 끝내지 않게
        setTimeout(function giveUpWaiting() {
          if (finishPending === resolve) finishPending = null;
          resolve();
        }, RENDER_TIMEOUT_MS);
      });
    },
    { direction },
  );
}

// update 가 새 화면을 DOM 에 반영하고 끝나면(Promise 면 resolve 되면) 스냅샷을 찍는다
function startStoryTransition(
  update: () => void | Promise<void>,
  {
    direction,
    from,
  }: { direction?: StoryDirection; from?: HTMLElement | null },
) {
  if (!('startViewTransition' in document) || prefersReducedMotion()) {
    void update();
    return;
  }

  const root = document.documentElement;
  // 옛 화면 스냅샷에서는 카드 사진이 미디어 이름을 갖는다
  if (from) from.style.viewTransitionName = MEDIA_NAME;
  if (direction) root.dataset.storyDirection = direction;

  const transition = document.startViewTransition(function renderNextStory() {
    // 새 화면에서는 모달 미디어만 이 이름을 가져야 한다(같은 이름이 둘이면 전환이 취소된다)
    if (from) from.style.viewTransitionName = '';
    return update();
  });

  // 스냅샷 실패·건너뜀은 전환만 포기하고 이동은 그대로 진행된다 — 받지 않으면 처리되지 않은 에러로 남는다
  transition.ready.catch(() => {});
  transition.finished.finally(function clearDirection() {
    delete root.dataset.storyDirection;
  });
}
